/**
 * Warum eine Session 0 Aura gebracht hat (Aura v2, Okt 2026).
 *
 * Das Backend entscheidet und liefert den Grund beim Abschluss als `auraReason`
 * (backend/src/services/SessionService.js, AURA_REASONS; null = Aura vergeben).
 * Bis dahin stand im Abschluss-Bildschirm immer derselbe Satz ("ab zwei
 * validierten Teilnehmenden"), auch wenn es am Tageslimit oder an der
 * Mindestdauer lag, und SessionStatus schaetzte die Aura-Zahl selbst.
 *
 * Fehlt das Feld (die Abschluss-Anzeige kommt beim manuellen Beenden VOR dem
 * Server-Abschluss, alte Antworten kennen es nicht), leitet
 * `inferAuraReason` nur das ab, was das Geraet sicher weiss: zu wenige
 * Validierte oder zu kurz. Alles andere bleibt offen statt geraten.
 */

export const AURA_REASONS = Object.freeze({
  AURA_DISABLED: 'AURA_DISABLED',
  SOLO: 'SOLO',
  DAILY_BUDGET: 'DAILY_BUDGET',
  UNDER_MIN_DURATION: 'UNDER_MIN_DURATION',
});

/** Satz, wenn der Grund unbekannt ist (Feld fehlt, unbekannter Wert) */
export const AURA_REASON_FALLBACK_TEXT = 'Für diese Session gibt es keine Aura.';

/** Satz, solange der Server die Session noch nicht abgeschlossen hat */
export const AURA_PENDING_TEXT = 'Wie viel Aura du bekommst, steht fest, sobald die Session abgeschlossen ist.';

/**
 * Erklaerung fuer 0 Aura.
 *
 * @param {string|null|undefined} reason - auraReason vom Server
 * @param {{ minSessionDuration?: number }} [options] - Mindestdauer der Schule in Minuten
 * @returns {string|null} null, wenn Aura vergeben wurde (reason === null)
 */
export const getAuraReasonText = (reason, { minSessionDuration } = {}) => {
  if (reason === null) return null;

  switch (reason) {
    case AURA_REASONS.UNDER_MIN_DURATION: {
      const min = Number(minSessionDuration);
      return Number.isFinite(min) && min > 0
        ? `Aura gibt es ab ${min} Minuten Lernzeit.`
        : 'Die Session war zu kurz für Aura.';
    }
    case AURA_REASONS.DAILY_BUDGET:
      return 'Dein Aura-Tageslimit ist für heute erreicht. Morgen geht es weiter.';
    case AURA_REASONS.AURA_DISABLED:
      return 'An deiner Schule ist Aura gerade ausgeschaltet.';
    case AURA_REASONS.SOLO:
      return 'Aura gibt es ab zwei validierten Teilnehmenden.';
    default:
      return AURA_REASON_FALLBACK_TEXT;
  }
};

/**
 * Rueckfall ohne Server-Grund: nur, was das Geraet sicher weiss.
 *
 * @param {{ validatedCount?: number, durationMinutes?: number, minSessionDuration?: number }} facts
 * @returns {string|undefined} SOLO, UNDER_MIN_DURATION oder undefined (unbekannt)
 */
export const inferAuraReason = ({ validatedCount, durationMinutes, minSessionDuration } = {}) => {
  if (Number.isFinite(validatedCount) && validatedCount < 2) return AURA_REASONS.SOLO;
  if (
    Number.isFinite(durationMinutes)
    && Number.isFinite(minSessionDuration)
    && durationMinutes < minSessionDuration
  ) {
    return AURA_REASONS.UNDER_MIN_DURATION;
  }
  return undefined;
};
